import { mapOptionSyncErrors } from '../../patterns';
import { AppointmentProposalOptionProduct } from './appointmentProposalOptionProducts.models';

type ProductIds = Pick<
  AppointmentProposalOptionProduct,
  'appointmentProposalOptionId' | 'appointmentProposalOptionProductId'
>;

export interface AppointmentProposalOptionProductsState {
  ids: string[];
  entities: Record<string, AppointmentProposalOptionProduct>;
  selectedProposalId: string | null;
  error: string | null;
  isLoaded: boolean;
  isLoading: boolean;
  loadedProposalIds: string[];
  isLoadingProductIds: string[];
}

export const initState: AppointmentProposalOptionProductsState = {
  ids: [],
  entities: {},
  selectedProposalId: null,
  error: null,
  isLoaded: false,
  isLoading: true,
  loadedProposalIds: [],
  isLoadingProductIds: [],
};

type Action =
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION_PRODUCTS';
      payload: {
        appointmentProposalId: string;
      };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION_PRODUCTS_SUCCESS';
      payload: {
        appointmentProposalOptionProducts: AppointmentProposalOptionProduct[];
        appointmentProposalId: string;
      };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_OPTION_PRODUCTS_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_PRODUCTS';
      payload: { appointmentProposalId: string };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_PRODUCTS_SUCCESS';
      payload: {
        appointmentProposalOptionProducts: AppointmentProposalOptionProduct[];
        appointmentProposalId: string;
      };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_PRODUCTS_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'CREATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT';
      payload: { pricebookId: string };
    }
  | {
      type: 'CREATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_SUCCESS';
      payload: {
        appointmentProposalOptionProduct: AppointmentProposalOptionProduct;
      };
    }
  | {
      type: 'CREATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_FAILURE';
      payload: { error: string; pricebookId: string };
    }
  | {
      type: 'CREATE_MANY_APPOINTMENT_PROPOSAL_OPTION_PRODUCT';
      payload: { pricebookIds: string[] };
    }
  | {
      type: 'CREATE_MANY_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_SUCCESS';
      payload: {
        pricebookIds: string[];
        appointmentProposalOptionProducts: AppointmentProposalOptionProduct[];
      };
    }
  | {
      type: 'CREATE_MANY_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_FAILURE';
      payload: { error: string; pricebookIds: string[] };
    }
  | {
      type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT';
      payload: {
        appointmentProposalOptionProductId: string;
        appointmentProposalOptionId: string;
        update: Partial<AppointmentProposalOptionProduct>;
      };
    }
  | {
      type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_SUCCESS';
      payload: {
        appointmentProposalOptionProduct: AppointmentProposalOptionProduct;
      };
    }
  | {
      type: 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_FAILURE';
      payload: {
        error: string;
        appointmentProposalOptionProductId: string;
        appointmentProposalOptionId: string;
      };
    }
  | {
      type: 'DELETE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT';
    }
  | {
      type: 'DELETE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_SUCCESS';
      payload: {
        appointmentProposalOptionId: string;
        appointmentProposalOptionProductId: string;
      };
    }
  | {
      type: 'DELETE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'UPSERT_PRODUCTS';
      payload: {
        appointmentProposalOptionProducts: AppointmentProposalOptionProduct[];
      };
    }
  | {
      type: 'UPDATE_PRODUCT';
      payload: {
        appointmentProposalOptionProduct: Partial<AppointmentProposalOptionProduct> &
          ProductIds;
      };
    }
  | {
      type: 'SET_PRODUCTS_MUTATING';
      payload: { creatingIds?: string[] };
    }
  | {
      type: 'CLEAR_PRODUCTS_MUTATING';
      payload: { creatingIds?: string[]; error?: string };
    }
  | {
      type: 'SELECT_PROPOSAL_PRODUCTS';
      payload: { appointmentProposalId: string };
    };

// TODO: remove this buildId and remvove from the cases
const buildId = (product: ProductIds) => {
  return [
    'OPTION',
    product.appointmentProposalOptionId,
    'PRODUCT',
    product.appointmentProposalOptionProductId,
  ].join('#');
};

export const appointmentProposalOptionProductsReducer = (
  state: AppointmentProposalOptionProductsState,
  action: Action
): AppointmentProposalOptionProductsState => {
  switch (action.type) {
    case 'FETCH_APPOINTMENT_PROPOSAL_PRODUCTS':
    case 'FETCH_APPOINTMENT_PROPOSAL_OPTION_PRODUCTS':
      return {
        ...state,
        error: null,
        isLoading: true,
        isLoaded: state.loadedProposalIds.includes(
          action.payload.appointmentProposalId
        ),
        selectedProposalId: action.payload.appointmentProposalId,
      };

    case 'FETCH_APPOINTMENT_PROPOSAL_PRODUCTS_SUCCESS':
    case 'FETCH_APPOINTMENT_PROPOSAL_OPTION_PRODUCTS_SUCCESS': {
      const fetchedProducts = action.payload.appointmentProposalOptionProducts;
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        loadedProposalIds: [
          ...new Set([
            ...state.loadedProposalIds,
            action.payload.appointmentProposalId,
          ]),
        ],
        ids: [
          ...new Set([...state.ids, ...fetchedProducts.map((a) => buildId(a))]),
        ],
        entities: fetchedProducts.reduce(
          (prev, curr) => ({
            ...prev,
            [buildId(curr)]: mapOptionSyncErrors(curr),
          }),
          state.entities
        ),
      };
    }

    case 'CREATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT':
      return {
        ...state,
        isLoadingProductIds: [
          ...new Set([
            ...state.isLoadingProductIds,
            action.payload.pricebookId,
          ]),
        ],
        error: null,
      };

    case 'CREATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_SUCCESS':
      return {
        ...state,
        isLoadingProductIds: state.isLoadingProductIds.filter(
          (id) =>
            id !== action.payload.appointmentProposalOptionProduct.pricebookId
        ),
        isLoading: false,
        isLoaded: true,
        error: null,
        ids: [
          ...new Set([
            ...state.ids,
            buildId(action.payload.appointmentProposalOptionProduct),
          ]),
        ],
        entities: {
          ...state.entities,
          [buildId(action.payload.appointmentProposalOptionProduct)]:
            mapOptionSyncErrors(
              action.payload.appointmentProposalOptionProduct
            ),
        },
      };

    case 'CREATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_FAILURE':
      return {
        ...state,
        isLoadingProductIds: state.isLoadingProductIds.filter(
          (id) => id !== action.payload.pricebookId
        ),
        error: action.payload.error,
      };

    case 'CREATE_MANY_APPOINTMENT_PROPOSAL_OPTION_PRODUCT':
      return {
        ...state,
        isLoadingProductIds: [
          ...new Set([
            ...state.isLoadingProductIds,
            ...action.payload.pricebookIds,
          ]),
        ],
        error: null,
      };

    case 'CREATE_MANY_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_SUCCESS':
      return {
        ...state,
        isLoadingProductIds: state.isLoadingProductIds.filter(
          (id) => !action.payload.pricebookIds.includes(id)
        ),
        ids: [
          ...new Set([
            ...state.ids,
            ...action.payload.appointmentProposalOptionProducts.map(buildId),
          ]),
        ],
        entities: action.payload.appointmentProposalOptionProducts.reduce(
          (prev, curr) => ({
            ...prev,
            [buildId(curr)]: mapOptionSyncErrors(curr),
          }),
          state.entities
        ),
        error: null,
      };

    case 'CREATE_MANY_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_FAILURE':
      return {
        ...state,
        isLoadingProductIds: state.isLoadingProductIds.filter(
          (id) => !action.payload.pricebookIds.includes(id)
        ),
        error: action.payload.error,
      };

    case 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT': {
      const productId = buildId({
        appointmentProposalOptionId: action.payload.appointmentProposalOptionId,
        appointmentProposalOptionProductId:
          action.payload.appointmentProposalOptionProductId,
      });
      return {
        ...state,
        error: null,
        isLoading: false,
        isLoaded: true,
        entities: {
          ...state.entities,
          [productId]: {
            ...state.entities[productId],
            ...action.payload.update,
          },
        },
      };
    }

    case 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_SUCCESS': {
      const original =
        state.entities[
          buildId(action.payload.appointmentProposalOptionProduct)
        ];

      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: null,
        entities: {
          ...state.entities,
          [buildId(action.payload.appointmentProposalOptionProduct)]: {
            ...original,
            ...mapOptionSyncErrors(
              action.payload.appointmentProposalOptionProduct
            ),
            hasMembership: original?.hasMembership ?? false,
            membershipPrice: original?.membershipPrice ?? 0,
          },
        },
      };
    }

    case 'DELETE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT':
      return {
        ...state,
        error: null,
        isLoading: true,
        isLoaded: true,
      };

    case 'DELETE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: null,
        ids: state.ids.filter((id) => id !== buildId(action.payload)),
        entities: {
          ...state.entities,
          [buildId(action.payload)]: {
            ...state.entities[buildId(action.payload)],
            status: 'DELETED',
          },
        },
      };

    case 'FETCH_APPOINTMENT_PROPOSAL_PRODUCTS_FAILURE':
    case 'FETCH_APPOINTMENT_PROPOSAL_OPTION_PRODUCTS_FAILURE':
    case 'UPDATE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_FAILURE':
    case 'DELETE_APPOINTMENT_PROPOSAL_OPTION_PRODUCT_FAILURE':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: action.payload.error,
      };

    case 'SELECT_PROPOSAL_PRODUCTS': {
      const isLoaded = state.loadedProposalIds.includes(
        action.payload.appointmentProposalId
      );
      return {
        ...state,
        error: null,
        isLoading: !isLoaded,
        isLoaded: isLoaded,
        selectedProposalId: action.payload.appointmentProposalId,
      };
    }

    case 'UPDATE_PRODUCT': {
      const product = action.payload.appointmentProposalOptionProduct;
      const productId = buildId(product);
      return {
        ...state,
        ids: [...new Set([...state.ids, productId])],
        entities: {
          ...state.entities,
          [productId]: { ...state.entities[productId], ...product },
        },
      };
    }

    case 'UPSERT_PRODUCTS': {
      const products = action.payload.appointmentProposalOptionProducts;
      const arrivedPricebookIds = products.map((p) => p.pricebookId);
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        isLoadingProductIds: state.isLoadingProductIds.filter(
          (id) => !arrivedPricebookIds.includes(id)
        ),
        loadedProposalIds: [
          ...new Set([
            ...state.loadedProposalIds,
            ...products.map((p) => p.appointmentProposalId),
          ]),
        ],
        ids: [...new Set([...state.ids, ...products.map((p) => buildId(p))])],
        entities: products.reduce(
          (prev, curr) => ({
            ...prev,
            [buildId(curr)]: {
              ...mapOptionSyncErrors(curr),
              status:
                prev[buildId(curr)]?.status === 'DELETED'
                  ? 'DELETED'
                  : curr.status,
            },
          }),
          state.entities
        ),
      };
    }

    case 'SET_PRODUCTS_MUTATING':
      return {
        ...state,
        error: null,
        isLoadingProductIds: action.payload.creatingIds
          ? [
              ...new Set([
                ...state.isLoadingProductIds,
                ...action.payload.creatingIds,
              ]),
            ]
          : state.isLoadingProductIds,
        isLoading: action.payload.creatingIds ? state.isLoading : true,
      };

    case 'CLEAR_PRODUCTS_MUTATING':
      return {
        ...state,
        error: action.payload.error ?? state.error,
        isLoadingProductIds: action.payload.creatingIds
          ? state.isLoadingProductIds.filter(
              (id) => !action.payload.creatingIds?.includes(id)
            )
          : state.isLoadingProductIds,
        isLoading: action.payload.creatingIds ? state.isLoading : false,
      };

    default:
      return state;
  }
};
